import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { AdvancedMarker, APIProvider, Map, InfoWindow } from '@vis.gl/react-google-maps';
import { getDisplayDateTimeFormat } from '../util/generalUtills';
import '../css/GoogleMapAllVehicle.css';
import { frontendURL } from '../axios';

const GoogleMapAllVehicle = ({ mapHeight, vehicleData }) => {
  const mapRef = useRef(null);
  const [currentLocation, setCurrentLocation] = useState(null); // State for user's location
  const [selectedVehicle, setSelectedVehicle] = useState(null); // Track clicked vehicle
  const [boundsFitted, setBoundsFitted] = useState(false); // Track if bounds have been fitted

  // Calculate default bounds from vehicleData
  const defaultBounds = useMemo(() => {
    if (!vehicleData || vehicleData.length === 0) {
      // Default bounds (India) if no vehicle data
      return {
        north: 37.0,
        south: 6.0,
        east: 97.0,
        west: 68.0
      };
    }

    const bounds = {
      north: -90,
      south: 90,
      east: -180,
      west: 180
    };

    let hasValidMarkers = false;

    vehicleData.forEach((vehicle) => {
      const lat = parseFloat(vehicle.lat);
      const lng = parseFloat(vehicle.lng);
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        bounds.north = Math.max(bounds.north, lat);
        bounds.south = Math.min(bounds.south, lat);
        bounds.east = Math.max(bounds.east, lng);
        bounds.west = Math.min(bounds.west, lng);
        hasValidMarkers = true;
      }
    });

    // If no valid markers, return default bounds
    if (!hasValidMarkers) {
      return {
        north: 37.0,
        south: 6.0,
        east: 97.0,
        west: 68.0
      };
    }

    // Add padding to bounds (10% on each side)
    const latPadding = (bounds.north - bounds.south) * 0.1;
    const lngPadding = (bounds.east - bounds.west) * 0.1;

    return {
      north: bounds.north + latPadding,
      south: bounds.south - latPadding,
      east: bounds.east + lngPadding,
      west: bounds.west - lngPadding
    };
  }, [vehicleData]);

  const fitMapBounds = useCallback(() => {
    if (!mapRef.current || !vehicleData || vehicleData.length === 0) {
      console.log("fitMapBounds: Missing map or vehicleData", { 
        hasMap: !!mapRef.current, 
        vehicleCount: vehicleData?.length 
      });
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidMarkers = false;

    // Add markers to the bounds
    vehicleData.forEach((vehicle) => {
      const lat = parseFloat(vehicle.lat);
      const lng = parseFloat(vehicle.lng);
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        const markerPosition = new window.google.maps.LatLng(lat, lng);
        bounds.extend(markerPosition);
        hasValidMarkers = true;
      }
    });

    console.log("fitMapBounds: Bounds calculated", { 
      hasValidMarkers, 
      isEmpty: bounds.isEmpty(),
      mapRefType: typeof mapRef.current,
      mapRefKeys: mapRef.current ? Object.keys(mapRef.current) : []
    });

    // After all markers are added, fit the map to the bounds with padding
    if (hasValidMarkers && !bounds.isEmpty()) {
      try {
        // Try different ways to access the map instance
        let mapInstance = null;
        
        // Method 1: Direct access
        if (mapRef.current && typeof mapRef.current.fitBounds === 'function') {
          mapInstance = mapRef.current;
        }
        // Method 2: Check for innerMap
        else if (mapRef.current?.innerMap && typeof mapRef.current.innerMap.fitBounds === 'function') {
          mapInstance = mapRef.current.innerMap;
        }
        // Method 3: Check for map property
        else if (mapRef.current?.map && typeof mapRef.current.map.fitBounds === 'function') {
          mapInstance = mapRef.current.map;
        }
        // Method 4: Check if it's the Google Maps instance directly
        else if (mapRef.current && mapRef.current.getBounds) {
          mapInstance = mapRef.current;
        }

        console.log("fitMapBounds: Map instance found", { 
          hasMapInstance: !!mapInstance,
          hasFitBounds: mapInstance && typeof mapInstance.fitBounds === 'function'
        });

        if (mapInstance && typeof mapInstance.fitBounds === 'function') {
          console.log("fitMapBounds: Calling fitBounds");
          mapInstance.fitBounds(bounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50
          });
          setBoundsFitted(true);
          console.log("fitMapBounds: Successfully fitted bounds");
        } else if (mapInstance && mapInstance.getBounds) {
          // Alternative: use setCenter and setZoom
          console.log("fitMapBounds: Using setCenter/setZoom fallback");
          const center = bounds.getCenter();
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latDiff = ne.lat() - sw.lat();
          const lngDiff = ne.lng() - sw.lng();
          
          // Calculate appropriate zoom level
          const latZoom = Math.log2(360 / latDiff);
          const lngZoom = Math.log2(360 / lngDiff);
          const zoom = Math.min(Math.floor(Math.min(latZoom, lngZoom)) - 1, 18);
          
          mapInstance.setCenter(center);
          mapInstance.setZoom(Math.max(zoom, 1));
          setBoundsFitted(true);
          console.log("fitMapBounds: Successfully set center and zoom");
        } else {
          console.warn("fitMapBounds: Could not find valid map instance");
        }
      } catch (error) {
        console.error("Error fitting bounds:", error);
      }
    }
  }, [vehicleData]);

  const onLoad = (map) => {
    console.log("onLoad: Map instance received", { 
      map, 
      type: typeof map,
      keys: map ? Object.keys(map) : [],
      hasFitBounds: map && typeof map.fitBounds === 'function',
      hasInnerMap: map && map.innerMap,
      hasMap: map && map.map
    });
    
    // Store the map instance - try to get the actual Google Maps instance
    if (map) {
      // Try different ways to get the actual Google Maps instance
      if (map.innerMap) {
        mapRef.current = map.innerMap;
      } else if (map.map) {
        mapRef.current = map.map;
      } else if (typeof map.fitBounds === 'function') {
        mapRef.current = map;
      } else {
        mapRef.current = map;
      }
    } else {
      mapRef.current = map;
    }

    console.log("onLoad: Stored map instance", { 
      mapRef: mapRef.current,
      hasFitBounds: mapRef.current && typeof mapRef.current.fitBounds === 'function'
    });

    // Only set the user's location after the map has loaded
    if (navigator.geolocation && !currentLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error fetching location:", error);
        }
      );
    }

    // Try to fit bounds when map loads
    if (vehicleData && vehicleData.length > 0 && !boundsFitted) {
      setTimeout(() => {
        fitMapBounds();
      }, 500);
    }
  };

  // Fit bounds when vehicleData changes (for first time loading)
  useEffect(() => {
    if (!mapRef.current || !vehicleData || vehicleData.length === 0 || boundsFitted) return;

    // Use multiple timeouts to ensure map and markers are fully rendered
    const timer1 = setTimeout(() => {
      fitMapBounds();
    }, 300);
    
    const timer2 = setTimeout(() => {
      fitMapBounds();
    }, 1000);
    
    const timer3 = setTimeout(() => {
      fitMapBounds();
    }, 2000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [vehicleData, boundsFitted, fitMapBounds]);

  const handleMarkerClick = (vehicle) => {
    setSelectedVehicle(vehicle); // Set the clicked vehicle to show the InfoWindow
  };

  // Create custom marker icon based on vehicle status
  const createCustomMarker = (vehicle) => {
    const isRunning = vehicle?.status === 'Running';
    const markerColor = isRunning ? 'blue' : 'red';
    
    return (
      <div 
        className="custom-marker"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        {/* <img 
          src={markerIcon} 
          alt={`${vehicle?.name} marker`}
          style={{
            width: '25px',
            height: '25px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
          }}
        /> */}
        <div
          style={{
            position: 'absolute',
            top: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${markerColor === 'blue' ? '#2196F3' : '#F44336'}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        >
          <img 
            src={`${frontendURL}user.png`} 
            alt="User"
            style={{
              width: '14px',
              height: '14px',
              objectFit: 'cover',
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ height: mapHeight }}>
      <APIProvider
        solutionChannel="GMP_devsite_samples_v3_rgmbasicmap"
        apiKey={`AIzaSyCzMU4VXRP_RFXHNKi15YlGAMAbflJ9dfU`}
      >
        <Map
          defaultBounds={defaultBounds}
          gestureHandling={"greedy"}
          disableDefaultUI={false}
          onLoad={onLoad}
          onIdle={() => {
            // Try to fit bounds when map is idle (fully loaded)
            if (vehicleData && vehicleData.length > 0 && !boundsFitted && mapRef.current) {
              console.log("onIdle: Attempting to fit bounds");
              setTimeout(() => {
                fitMapBounds();
              }, 300);
            }
          }}
          mapId="brain_bird_map_id" // Add your Map ID here
        >
          {vehicleData.map((vehicle, index) => (
            parseFloat(vehicle.lat) > 0 && parseFloat(vehicle.lng) > 0 && (
              <AdvancedMarker
                key={index}
                position={{ lat: parseFloat(vehicle.lat), lng: parseFloat(vehicle.lng) }}
                onClick={() => handleMarkerClick(vehicle)} // Open InfoWindow on marker click
              >
                {createCustomMarker(vehicle)}
                {selectedVehicle && selectedVehicle.id === vehicle.id && (
                  <InfoWindow
                    position={{ lat: parseFloat(vehicle.lat), lng: parseFloat(vehicle.lng) }}
                    onCloseClick={() => setSelectedVehicle(null)} // Close InfoWindow
                  >
                    <div className="info-window">
                      <div className="info-window-header">
                        <h3>{vehicle?.name} ({vehicle?.adminUserType?.name})</h3>
                        <div className="status-icon">
                          {vehicle?.status === 'Running' ? (
                            <span className="status running">🚗</span>
                          ) : (
                            <span className="status stopped">🛑</span>
                          )}
                        </div>
                      </div>
                      <div className="info-window-content">
                        <p><strong>Contact:</strong> {vehicle?.phone}</p>
                        <p><strong>Status:</strong> {vehicle?.status}</p>
                        <p><strong>Speed:</strong> {vehicle?.speed} km/h</p>
                        <p><strong>Accuracy:</strong> {vehicle?.accuracy} m</p>
                        <p><strong>Last Location:</strong> {getDisplayDateTimeFormat(vehicle?.lastLocation)}</p>
                        <p><strong>Address:</strong> {vehicle?.address}</p>
                        <p><strong>City:</strong> {vehicle?.citiesAdminUsers?.city_name}</p>
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </AdvancedMarker>
            )
          ))}
        </Map>
      </APIProvider>
    </div>
  );
};

export default GoogleMapAllVehicle;
